<script setup lang="ts">
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {useI18n} from 'vue-i18n'

export interface SearchFilterListItem {
  id: string
  label: string
  count: number
  iconNameType: IconNameType
}

interface Props {
  modelValue: string
  filters: SearchFilterListItem[]
}

defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const {t} = useI18n()
</script>

<template>
  <aside class="search-filter-list">
    <div class="filters-title">{{t('general.filter')}}</div>
    <div class="filter-list">
      <button
        v-for="filterItem in filters"
        :key="filterItem.id"
        type="button"
        class="filter-item"
        :class="{'is-active': filterItem.id === modelValue}"
        @click="emit('update:modelValue', filterItem.id)"
      >
        <span class="filter-item-left">
          <span class="filter-radio" :class="{'is-checked': filterItem.id === modelValue}" aria-hidden="true"/>
          <IconComponent class="filter-icon" :name="filterItem.iconNameType"/>
          <span class="filter-label">{{filterItem.label}}</span>
        </span>
        <span class="filter-count">{{filterItem.count}}</span>
      </button>
    </div>
  </aside>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;

.search-filter-list {
  display: grid;
  align-content: flex-start;
  gap: map-get(variables.$spacing, space-3);
  min-width: 0;
}

.filters-title {
  font-size: map-get(variables.$textSizes, text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: map-get(variables.$colors, content-dim);
}

.filter-list {
  display: grid;
  gap: map-get(variables.$spacing, space-1-5);
}

.filter-item-left {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-2);
  min-width: 0;
}

.filter-radio {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: map-get(variables.$radiuses, radius-full);
  border: 2px solid map-get(variables.$colors, container-high);
  box-sizing: border-box;
  position: relative;
  flex-shrink: 0;

  &.is-checked {
    border-color: map-get(variables.$colors, primary);

    &::after {
      content: '';
      position: absolute;
      inset: 0.18rem;
      border-radius: map-get(variables.$radiuses, radius-full);
      background: map-get(variables.$colors, primary);
    }
  }
}

.filter-icon {
  flex-shrink: 0;
}

.filter-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-count {
  flex-shrink: 0;
}

.filter-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: map-get(variables.$spacing, space-2);
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 0;
  border-radius: variables.$borderRadius;
  background: transparent;
  color: map-get(variables.$colors, content-dim);
  cursor: pointer;

  &.is-active {
    background: map-get(variables.$colors, container-low);
    color: map-get(variables.$colors, content-normal);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: map-get(variables.$colors, container-low);
      color: map-get(variables.$colors, content-normal);
    }
  }
}
</style>
